import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { GlobalTimerWidget } from '@/features/time-tracking/public';
import { GlobalTaskDialog } from '@/features/tasks/public';

import { AuthGuard } from '@/core/guards/AuthGuard';
import { OrganizationGuard } from '@/core/guards/OrganizationGuard';
import { Layout } from '@/shared/components/custom/layout/Layout';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { ROUTES } from '@/core/constants/routes';
import { PageSkeleton } from '@/shared/components/custom/page-skeleton/PageSkeleton';

import {
  AuthLayout,
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  VerifyRecoveryCodePage,
  ResetPasswordPage,
  AuthCallbackPage,
} from '@/features/auth/public';
import { NotificationsProvider } from '@/features/notifications/public';

const InvitationAcceptPage = lazy(() =>
  import('@/features/invitations/public').then((m) => ({ default: m.InvitationAcceptPage })),
);
const OnboardingPage = lazy(() =>
  import('@/features/organizations/public').then((m) => ({ default: m.OnboardingPage })),
);
const MembersPage = lazy(() =>
  import('@/features/members/public').then((m) => ({ default: m.MembersPage })),
);
const ClientsPage = lazy(() =>
  import('@/features/clients/public').then((m) => ({ default: m.ClientsPage })),
);
const ClientMembersPage = lazy(() =>
  import('@/features/client-members/public').then((m) => ({ default: m.ClientMembersPage })),
);
const CalendarPage = lazy(() =>
  import('@/features/calendar/public').then((m) => ({ default: m.CalendarPage })),
);
const ProjectBoardPage = lazy(() =>
  import('@/features/projects/public').then((m) => ({ default: m.ProjectBoardPage })),
);
const BoardPage = lazy(() =>
  import('@/features/boards/public').then((m) => ({ default: m.BoardPage })),
);
const OrganizationUnitsPage = lazy(() =>
  import('@/features/organization-units/public').then((m) => ({
    default: m.OrganizationUnitsPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/public').then((m) => ({ default: m.SettingsPage })),
);
const ReportsPage = lazy(() =>
  import('@/features/reports/public').then((m) => ({ default: m.ReportsPage })),
);
const LaborPerformancePage = lazy(() =>
  import('@/features/labor-performance/public').then((m) => ({ default: m.LaborPerformancePage })),
);
const TenantDashboardPage = lazy(() => import('@/features/dashboard/pages/TenantDashboardPage'));
// ProjectDashboardPage is intentionally disabled — project-level dashboard is replaced by board-level dashboards.
// const ProjectDashboardPage = lazy(() => import('@/features/dashboard/pages/ProjectDashboardPage'));
const OrgSummaryPage = lazy(() =>
  import('@/features/analytics/public').then((m) => ({ default: m.OrgSummaryPage })),
);
const TaskRedirectPage = lazy(() =>
  import('@/features/tasks/public').then((m) => ({ default: m.TaskRedirectPage })),
);
const FormsListPage = lazy(() =>
  import('@/features/forms/public').then((m) => ({ default: m.FormsListPage })),
);
const FormEditorPage = lazy(() =>
  import('@/features/forms/public').then((m) => ({ default: m.FormEditorPage })),
);
const TemplateEditorPage = lazy(() =>
  import('@/features/forms/public').then((m) => ({ default: m.TemplateEditorPage })),
);
const FormResponsePage = lazy(() =>
  import('@/features/forms/public').then((m) => ({ default: m.FormResponsePage })),
);
const PublicFormSubmissionPage = lazy(() =>
  import('@/features/forms/public').then((m) => ({ default: m.PublicFormSubmissionPage })),
);
const FormAccessByTokenPage = lazy(() =>
  import('@/features/forms/public').then((m) => ({ default: m.FormAccessByTokenPage })),
);
const NoteEditorPage = lazy(() =>
  import('@/features/notes/public').then((m) => ({ default: m.NoteEditorPage })),
);
const NotesEmptyPlaceholder = lazy(() =>
  import('@/features/notes/public').then((m) => ({ default: m.NotesEmptyPlaceholder })),
);
const PersonalSummaryPage = lazy(() =>
  import('@/features/personal-summary/public').then((m) => ({
    default: m.PersonalSummaryPage,
  })),
);
const TimeOffPage = lazy(() =>
  import('@/features/time-off/public').then((m) => ({ default: m.TimeOffPage })),
);

const wrap = (node: React.ReactNode) => (
  <Suspense fallback={<PageSkeleton />}>
    <FeatureErrorBoundary reporter={sentryReporter}>{node}</FeatureErrorBoundary>
  </Suspense>
);

export const router = createBrowserRouter([
  // ─── Rutas públicas ───────────────────────────────────────────────────────
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: wrap(<LoginPage />) },
      { path: '/register', element: wrap(<RegisterPage />) },
      { path: '/forgot-password', element: wrap(<ForgotPasswordPage />) },
      { path: '/verify-recovery-code', element: wrap(<VerifyRecoveryCodePage />) },
      { path: '/reset-password', element: wrap(<ResetPasswordPage />) },
    ],
  },

  // ─── Callback OAuth ───────────────────────────────────────────────────────
  {
    path: '/auth/callback',
    element: wrap(<AuthCallbackPage />),
  },

  // ─── Formulario público — sin auth, sin organización ─────────────────────
  {
    path: '/f/access/:token',
    element: wrap(<FormAccessByTokenPage />),
  },
  {
    path: '/f/:formId',
    element: wrap(<PublicFormSubmissionPage />),
  },

  // ─── Rutas protegidas ─────────────────────────────────────────────────────
  {
    element: <AuthGuard />,
    children: [
      {
        element: <OrganizationGuard />,
        children: [
          {
            path: ROUTES.invitationsAccept,
            element: wrap(<InvitationAcceptPage />),
          },
          {
            element: (
              <NotificationsProvider>
                <Layout headerTimerSlot={<GlobalTimerWidget />} />
                <GlobalTaskDialog />
              </NotificationsProvider>
            ),
            children: [
              { path: '/onboarding', element: wrap(<OnboardingPage />) },
              { path: '/:orgId/projects/:projectId/boards', element: wrap(<ProjectBoardPage />) },
              {
                path: '/:orgId/projects/:projectId/boards/:boardId',
                element: wrap(<BoardPage />),
              },
              { path: '/:orgId/dashboard', element: wrap(<TenantDashboardPage />) },
              { path: '/:orgId/tasks/:taskId', element: wrap(<TaskRedirectPage />) },
              // Project-level dashboard route disabled — replaced by board-level dashboards.
              // { path: '/:orgId/projects/:projectId/dashboard', element: wrap(<ProjectDashboardPage />) },
              { path: '/:orgId/labor-performance', element: wrap(<LaborPerformancePage />) },
              { path: '/:orgId/analytics', element: wrap(<OrgSummaryPage />) },
              { path: '/:orgId/members', element: wrap(<MembersPage />) },
              { path: '/:orgId/reports', element: wrap(<ReportsPage />) },
              { path: '/:orgId/clients', element: wrap(<ClientsPage />) },
              {
                path: '/:orgId/clients/:clientId/members',
                element: wrap(<ClientMembersPage />),
              },
              { path: '/:orgId/calendars', element: wrap(<CalendarPage />) },
              { path: '/:orgId/forms', element: wrap(<FormsListPage />) },
              {
                path: '/:orgId/forms/templates/:templateId',
                element: wrap(<TemplateEditorPage />),
              },
              { path: '/:orgId/forms/:formId/respond', element: wrap(<FormResponsePage />) },
              { path: '/:orgId/forms/:formId', element: wrap(<FormEditorPage />) },
              { path: '/:orgId/notes', element: wrap(<NotesEmptyPlaceholder />) },
              { path: '/:orgId/notes/:noteId', element: wrap(<NoteEditorPage />) },
              { path: '/:orgId/personal-summary', element: wrap(<PersonalSummaryPage />) },
              { path: '/:orgId/time-off', element: wrap(<TimeOffPage />) },
              { path: '/:orgId/time-off/requests/:requestId', element: wrap(<TimeOffPage />) },
              { path: '/:orgId/organization-units', element: wrap(<OrganizationUnitsPage />) },
              { path: '/:orgId/settings', element: wrap(<SettingsPage />) },
            ],
          },
        ],
      },
    ],
  },

  // ─── Raíz y wildcard ──────────────────────────────────────────────────────
  { path: '/', element: <Navigate to="/login" replace /> },
  { path: '*', element: <Navigate to="/login" replace /> },
]);
